import React from 'react';
import Highcharts, { Chart, DrilldownEventObject, Options } from 'highcharts';
import { FieldType, RulesConfiguration } from 'controls/queryBuilder/ruleDefinition';
import { apiFetch, ApiRequest, PagedList } from 'framework/api';
import { GridDataItem } from 'controls/grid/gridDataItem';
import { IntervalType, TimePeriod } from 'controls/react/form/timePeriodSelector';
import { TimePeriodType } from 'controls/react/form/timePeriodType';
import { GridStore } from 'controls/grid/gridStore';
import { WidgetProps } from 'controls/designer/features/widgets/widgetProps';
import { computed, makeAutoObservable, makeObservable, toJS } from 'mobx';
import { debounce, throttle, uniq } from 'lodash';
import Configuration from 'configuration';
import { formatNumber } from 'tools/helpers/math';
import { ModalPosition, openModal } from 'controls/react/ant/antModal';
import {
	DrilledDownModal,
	DrilledDownWidgetStore,
} from 'controls/designer/features/widgets/datasources/datasourceDrilleddownGrid';
import { Color } from 'react-color';
import { ColorPalette, DEFAULT_COLOR_PALETTE } from './../colorPalette';
import {
	DatasourceConfig,
	DatasourceConfigModelBase,
} from 'controls/designer/features/widgets/datasources/shared/datasourceConfigBase';
import {
	CategoryColumnName,
	PSEUDO_FULL_RESPONSE,
} from 'controls/designer/features/widgets/datasources/shared/constants';
import {
	getTimeColumns,
	applyTimeColumnDefaults,
} from 'controls/designer/features/widgets/datasources/shared/components/time-period-settings-section/utils';
import { newGuid } from 'tools/guid';
import {
	ChartTypeOptions,
	CommonSettingsDatasourceConfigStore,
	getBarSubTypeOptions,
	getChartTypeOptions,
	getPieSubTypeOptions,
} from 'controls/designer/features/widgets/datasources/shared/commonSettings';
import { RemoteEventsManager } from 'core/remoteEventsManager';
import { DataSourceEvent } from 'framework/entities/events';
import { MobxManager, ModelValidator } from 'framework/mobx-integration';
import { clearDatasourceCache, getDataSourceData, getDataSourceFiltersConfiguration, getDataSources } from './api';
import { WidgetConfigurationProps } from 'controls/designer/features/widgets/widgetConfig';
import { DataSource } from 'areas/data-sources/types/dataSource';
import { PivotManager } from 'controls/designer/features/widgets/datasources/shared/utils/pivot-manager/pivotManager';
import { PivotModel } from 'controls/designer/features/widgets/datasources/shared/utils/pivot-manager/types';
import { DataSourceManager } from 'controls/designer/features/widgets/datasources/shared/utils/data-source-manager/dataSourceManager';
import { RedirectType } from 'controls/designer/features/redirectOnClick/redirectType';
import { translator } from 'core/localization';

const i18n = translator({
	'Pivot to Data Rows: Displays values from datasource rows instead of displaying them by columns.': {
		en: 'Pivot to Data Rows: Displays values from datasource rows instead of displaying them by columns.',
		no: 'Roter til Data Rader: Viser verdier fra datakilderader i stedet for å vise dem per kolonne.',
	},
	'Datasource chart': {
		en: 'Datasource chart',
		no: 'Datakilde diagram',
	},
	'Pie': {
		en: 'Pie',
		no: 'Pie',
	},
	'3D Pie': {
		en: '3D Pie',
		no: '3D Pie',
	},
	'Vertical': {
		en: 'Vertical',
		no: 'Vertikal',
	},
	'Horizontal': {
		en: 'Horizontal',
		no: 'Horisontal',
	},
	'Chart': {
		en: 'Chart',
		no: 'Diagram',
	},
	'Chart type': {
		en: 'Chart type',
		no: 'Diagramtype',
	},
	'Bar chart': {
		en: 'Bar chart',
		no: 'Søylediagram',
	},
	'Pie chart': {
		en: 'Pie chart',
		no: 'Sektordiagram',
	},
	'Vertical stacked': {
		en: 'Vertical stacked',
		no: 'Vertikalt stablet',
	},
	'Horizontal stacked': {
		en: 'Horizontal stacked',
		no: 'Horisontalt stablet',
	},
	'Donut': {
		en: 'Donut',
		no: 'Smultring',
	},
	'Display': {
		en: 'Display',
		no: 'Visning',
	},
	'Data not found': {
		en: 'Data not found',
		no: 'Data ikke funnet',
	},
	'Pivot data': {
		en: 'Pivot data',
		no: 'Pivoter data',
	},
	'Pivot data lets you turn the datasource table into a summary (pivot) grid.': {
		en: 'Pivot data lets you turn the datasource table into a summary (pivoted) grid.',
		no: 'Pivoter data lar deg gjøre datakilde tabellen om til et sammendrag (pivot-tabell).',
	},
	'Color': {
		en: 'color',
		no: 'Farge',
	},
	'Column': {
		en: 'Column',
		no: 'Kolonne',
	},
	'Column: Unique values in this field become headers and are used to aggregate values.': {
		en: 'Column: Unique values in this field become headers and are used to aggregate values.',
		no: 'Kolonne: Unike verdier i dette feltet blir kolonneoverskrifter og brukes til å aggregere verdier.',
	},
	'Value': {
		en: 'Value',
		no: 'Verdi',
	},
	'Values: The numeric field(s) to summarize.': {
		en: 'Values: The numeric field(s) to summarize.',
		no: 'De numeriske feltene som skal oppsummeres.',
	},
	'Color range': {
		en: 'Color range',
		no: 'Fargespekteret',
	},
	'Custom colors': {
		en: 'Custom colors',
		no: 'Tilpasset farger',
	},
	'Row label': {
		en: 'Row label',
		no: 'Rad navn',
	},
	'Row label (index): Selected column(s) define the categories that appear on your chart.': {
		en: 'Row label (index): Selected column(s) define the categories that appear in your chart.',
		no: 'Radetikett (indeks): Valgte kolonner definerer kategoriene som vises i diagrammet.',
	},
	'Hide Legend': {
		en: 'Hide legend',
		no: 'Skjul tegnforklaring',
	},
	'Number': {
		en: 'Number',
		no: 'Tall',
	},
	'Text': {
		en: 'Text',
		no: 'Tekst',
	},
	'Invert': {
		en: 'Invert',
		no: 'Inverter',
	},
	'Row labels are required': {
		en: 'Row labels are required',
		no: 'Rad navn er påkrevd',
	},
	'Show datasource grid on click': {
		en: 'Show datasource grid on click',
		no: 'Vis datasource kilde på klikk',
	},
	'Drilldown Datasource': {
		en: 'Drilldown datasource',
		no: 'Drilldown datakilde',
	},
	'Use Chart Datasource (set above)': {
		en: 'Use chart datasource (set above)',
		no: 'Bruk diagram datakilde (valgt over)',
	},
	'Split values': {
		en: 'Split values',
		no: 'Splitt verdier',
	},
	'Split values to separate chart columns': {
		en: 'Split values to separate chart columns',
		no: 'Split verdier til egne diagram stolper',
	},
	'Export to CSV': {
		en: 'Export to CSV',
		no: 'Eksporter til CSV',
	},
	'Decimal Numbers': {
		en: 'Decimal numbers',
		no: 'Desimaltall',
	},
	'Time': {
		en: 'Time',
		no: 'Tid',
	},
	'When disabled, the default time period from the datasource will be applied': {
		en: 'When inactive, the default time period from the datasource will be applied',
		no: 'Når denne er deaktivert, brukes standard tidsperiode fra datakilden',
	},
	'The default for {0} is {1}': {
		en: 'The default for {0} is {1}',
		no: 'Standard for {0} er {1}',
	},
});

export type ColorRow = { name: string; color: Color; key: string };
export type DataSourcesChartWidgetConfig = {
	chartType: string;
	chartSubType: string;
	legendColors: ColorRow[];
	colorPalette: ColorPalette;
	decimalsNumber: number;
	hideLegend: boolean;
	period: TimePeriod;
	hideTimePeriodSelector: boolean;
	timePeriodColumn?: string;
} & DatasourceConfig;

export class DataSourcesChartWidgetConfigModel implements DatasourceConfigModelBase<DataSourcesChartWidgetConfigModel> {
	id: string;
	type: string;
	title: string;
	dataSourceId: string;
	drilldownDataSourceId: string;
	accountId: string;
	dataSourceSelectorAccountId: string | null;
	chartType: string;
	// not pivoted
	categoryColumns: string[];
	dataColumns: string[];
	dataRows: string[];
	// pivoted
	pivotRows: string[];
	pivotColumns: string[];
	pivotValues: string[];
	//-----
	chartSubType: string;
	legendColors: ColorRow[];
	colorPalette: ColorPalette;
	decimalsNumber: number;
	pivotData: boolean;
	pivotMatrix: boolean;
	splitPivotValues: boolean;
	hideLegend: boolean;
	enableDrillDown: boolean = true;
	period: TimePeriod;
	hideTimePeriodSelector: boolean;
	timePeriodColumn: string;

	validator = new ModelValidator(this);
	mobx = new MobxManager();

	constructor(model: Partial<DataSourcesChartWidgetConfig>) {
		// for some reason there is appeared fields which not exist in a model (column: true). I can't reproduce it to understand where it comes from...
		const config = { ...model };
		Object.keys(config)
			.filter((key) => !Object.keys(this).includes(key))
			.map((key) => key as keyof DataSourcesChartWidgetConfig)
			.forEach((key) => delete config[key]);
		Object.assign(this, config);

		this.colorPalette = new ColorPalette(model.colorPalette ?? DEFAULT_COLOR_PALETTE);
		makeAutoObservable(this);
		this.validator
			.required('dataSourceId')
			.add('dataSourceSelectorAccountId', {
				message: i18n('Required field'),
				// null means shared datasources, only undefined is an error
				callback: () => this.dataSourceSelectorAccountId !== undefined,
			})
			.required('chartType')
			.required('chartSubType')
			.required('categoryColumns', () => !this.pivotMatrix, i18n('Row labels are required'), false)
			.required('pivotRows', () => this.pivotMatrix, i18n('Row labels are required'), false);

		this.mobx.reaction(
			() => this.dataSourceSelectorAccountId,
			() => (this.dataSourceId = undefined),
		);
		this.mobx.reaction(
			() => this.dataSourceId,
			() => {
				this.dataColumns = null;
				this.dataRows = null;
				this.categoryColumns = null;

				this.pivotColumns = null;
				this.pivotRows = null;
				this.pivotValues = null;
			},
		);
		this.mobx.reaction(
			() => this.chartType,
			() => (this.chartSubType = undefined),
		);
		this.mobx.reaction(
			() => this.pivotMatrix,
			() => {
				this.dataColumns = null;
				this.dataRows = null;
				this.categoryColumns = null;

				this.pivotColumns = null;
				this.pivotRows = null;
				this.pivotValues = null;
			},
		);
		this.mobx.reaction(
			() => this.enableDrillDown,
			(val: boolean) => {
				if (!val) this.drilldownDataSourceId = undefined;
			},
		);
		this.mobx.reaction(
			() => this.dataSourceId,
			() => {
				if (!this.drilldownDataSourceId) {
					this.drilldownDataSourceId = this.dataSourceId;
				}
			},
		);
	}

	getConfig() {
		const { mobx, validator, ...config } = toJS(this);
		return config;
	}

	destroy() {
		this.validator.destroy();
		this.mobx.destroy();
	}
}

export class DataSourcesChartWidgetStore {
	loading: boolean = false;
	errorMessage: string;
	columnsWarningMessage: string;
	rowsWarningMessage: string;
	chartContainerRef = React.createRef<HTMLDivElement>();
	chart: Highcharts.Chart;
	dataBuilders: { [id: string]: (e?: DrilldownEventObject) => Promise<any[]> } = {};
	drillDownRequestPayloadBuilders: {
		[id: string]: (key: string, e: DrilldownEventObject) => { [key: string]: any };
	} = {};
	datasourceRuleConfig: RulesConfiguration;
	data: PagedList<GridDataItem<any>>;

	gridStore: GridStore<any>;

	initialized: boolean = false;
	requestId: string;
	unsubscriber: { subscriberId: any; unsubscribe: () => void; releaseEvents: (data: any) => void };

	get timePeriod() {
		return this.props.config.period;
	}
	set timePeriod(value: TimePeriod) {
		this.props.config.period = value;
	}

	get config() {
		return this.props.config;
	}

	get enableDrillDown() {
		return this.props.config.enableDrillDown ?? true;
	}

	get hasCustomRedirectConfig() {
		const type = this.props.redirectConfig?.type;
		return type != null && type != RedirectType.Default && type != RedirectType.None;
	}

	private subTypes: any[];
	mobx = new MobxManager();

	constructor(private props: WidgetProps<DataSourcesChartWidgetConfig>) {
		this.dataBuilders['column'] = this.buildBarData;
		this.dataBuilders['bar'] = this.buildBarData;
		this.dataBuilders['pie'] = this.buildPieData;
		this.drillDownRequestPayloadBuilders['column'] = this.buildBarDataPayload;
		this.drillDownRequestPayloadBuilders['bar'] = this.buildBarDataPayload;
		this.drillDownRequestPayloadBuilders['pie'] = this.buildPieDataPayload;
		this.subTypes = [...getPieSubTypeOptions(), ...getBarSubTypeOptions()];
		this.requestId = newGuid();
		makeAutoObservable(this);
		this.mobx.reaction(() => this.timePeriod, this.reinitChart);
		this.mobx.reaction(
			() => this.props.designer.store.timeSettings?.period,
			(period) => this.updateTimeSettings(period),
			{ fireImmediately: true },
		);
	}

	init = async () => {
		this.fixLegacyColors();
		const confRequest = this.getDataSourceFiltersConfiguration();
		const datasourceRuleConfig = await apiFetch(confRequest);
		if (datasourceRuleConfig.success) {
			this.datasourceRuleConfig = datasourceRuleConfig.data;
		} else {
			this.errorMessage = uniq([datasourceRuleConfig.message])
				.filter((x) => !!x)
				.map((x) => x.replace('Not found exception', i18n('Data not found')))
				.join(';\r\n');
		}
		await this.loadData();
		this.subscribe();
		this.initialized = true;
	};

	loadData = async () => {
		const dataRequest = this.getDataRequest();
		const data = await apiFetch(dataRequest);

		if (data.data?.warning) {
			this.columnsWarningMessage = data.data.warning;
		}
		if (data.data?.total > data.data?.maxRows) {
			this.rowsWarningMessage = i18n('ROWS_WARNING_MESSAGE', data.data.maxRows, data.data.total);
		}

		if (data.success) {
			this.data = data.data;
		} else {
			this.errorMessage = uniq([data.message])
				.filter((x) => !!x)
				.map((x) => x.replace('Not found exception', i18n('Data not found')))
				.join(';\r\n');
		}
		if (this.data) {
			await this.drawChart();
		}
	};

	subscribe() {
		this.unsubscriber?.unsubscribe();

		const subscriptionObj = [DataSourceEvent.subscription(this.getDatasourceId(), this.config.accountId)];
		this.unsubscriber = RemoteEventsManager.subscribeCallback(subscriptionObj, this.onEvent);
	}

	onEvent = () => {
		this.destroyChartSafely();
		this.loadData();
	};

	reinitChart = async () => {
		this.destroyChartSafely();
		await this.init();
	};

	updateTimeSettings = (period: TimePeriod) => {
		if (period) {
			this.onPeriodChange(period);
		}
	};

	getDatasourceId(isDrillDown: boolean = false) {
		return this.enableDrillDown && this.props.config.drilldownDataSourceId && isDrillDown
			? this.props.config.drilldownDataSourceId
			: this.props.config.dataSourceId;
	}

	getTimePeriodColumn(isDrillDown: boolean = false) {
		return this.getDatasourceId(isDrillDown) == this.props.config.dataSourceId
			? this.props.config.timePeriodColumn
			: undefined;
	}

	fixLegacyColors = () => {
		if (!this.props.config.legendColors?.length) {
			return;
		}
		this.props.config.legendColors = this.props.config.legendColors.map((x) => ({
			name: x.name,
			color: (x.color as any)?.hex ?? x.color,
			key: x.key,
		}));
	};

	drawChart = async () => {
		const series = (await this.dataBuilders[this.props.config.chartType]?.()) ?? [];

		series.forEach((x) => Object.assign(x, this.seriesDefaults));

		const categories = this.data.items.map((row) => row.data[CategoryColumnName]);
		if (this.chart) {
			this.destroyChartSafely();
		}
		this.chart = new Highcharts.Chart(this.chartContainerRef.current, this.commonChartSettings(series, categories));

		// workaround to remove all option color. Seems like highcharts does not support that out of the box
		$(this.chartContainerRef.current).find('.chart-all-option').closest('g').find('rect').remove();
		$(this.chartContainerRef.current).find('.highcharts-3d-frame').closest('g').remove();
	};

	onPeriodChange = (period: TimePeriod) => {
		if (JSON.stringify(this.props.config.period) == JSON.stringify(period)) {
			return;
		}
		this.timePeriod = period;
	};

	onPeriodChangeDebounced = debounce(this.onPeriodChange, 1000);

	get chartType(): string {
		return this.subTypeData?.type ?? 'column';
	}

	get options3d() {
		return this.subTypeData?.chart3DOptions;
	}

	get subTypeData() {
		return this.subTypes.find((x) => x.value === this.props.config.chartSubType);
	}

	get seriesDefaults() {
		return this.subTypeData?.series ?? {};
	}

	destroy() {
		this.destroyChartSafely();
		this.unsubscriber?.unsubscribe();
		apiFetch(clearDatasourceCache({ accountId: this.config.accountId, requestId: this.requestId }));
	}

	private destroyChartSafely = () => {
		const chart = this.chart;
		this.chart = undefined;
		if (!chart) {
			return;
		}

		// Highcharts may throw in destroy() for partially initialized charts.
		if (!chart.renderer) {
			return;
		}

		try {
			chart.destroy();
		} catch {
			// Ignore destroy race errors and let next draw recreate the chart.
		}
	};

	commonChartSettings = (chartSeries: any[], categories: string[]): Options => {
		const store = this;
		return {
			chart: {
				type: this.chartType,
				options3d: this.options3d,
				backgroundColor: null,
				events: {
					drilldown: function (this: Chart, e: DrilldownEventObject) {
						if (!store.enableDrillDown) {
							return;
						}
						store.onDrillDownGridEditionThrottled(this, e);
					},
				},
			},
			title: {
				text: null,
			},
			credits: {
				enabled: false,
			},
			exporting: Configuration.highcharts.exporting,
			legend: {
				enabled: !this.props.config.hideLegend,
				align: 'right',
				verticalAlign: 'top',
				backgroundColor: Highcharts.defaultOptions.legend.backgroundColor || 'none',
				shadow: false,
				labelFormatter: function () {
					return `${this.name}`;
				},
			},
			xAxis: {
				categories: categories,
			},
			yAxis: {
				title: { text: '' },
			},
			tooltip: {
				backgroundColor: 'white',
				borderWidth: 0,
				shadow: {
					offsetX: 0,
					offsetY: 0,
					opacity: 1,
					width: 16,
					color: 'rgb(0,0,0,0.01)',
				},
				useHTML: true,
				headerFormat: '<b>{point.x}</b><br/>',
				formatter: function () {
					const d = this as unknown as { point: { name: string; y: number } };
					return `${d.point.name ?? this.series.name}<br/>
					${i18n('Value')}: ${formatNumber(d.point.y, store.props.config.decimalsNumber)}`;
				},
			},
			plotOptions: {
				bar: {
					pointWidth: 20,
					groupPadding: 0.001,
				},
				pie: {
					allowPointSelect: true,
					cursor: 'pointer',
					depth: 35,
					dataLabels: {
						enabled: true,
					},
					showInLegend: true,
				},
				column: {
					stacking: this.chartType == 'pie' ? undefined : 'normal',
					dataLabels: {
						formatter: function () {
							const formattedNumber = formatNumber(this.y, store.props.config.decimalsNumber);
							if (parseInt(formattedNumber) === 0 && formattedNumber % 1 === 0) {
								return null;
							} else {
								return formattedNumber;
							}
						},
						style: {
							textOutline: 'none',
						},
					},
					groupPadding: 0.03,
				},
				series: {
					stacking: this.chartType == 'pie' ? undefined : 'normal',
					dataLabels: {
						formatter: function () {
							const formattedNumber = formatNumber(this.y, store.props.config.decimalsNumber);
							if (parseInt(formattedNumber) === 0 && formattedNumber % 1 === 0) {
								return null;
							} else {
								return formattedNumber;
							}
						},
						style: {
							textOutline: 'none',
						},
					},
				},
			},
			series: chartSeries,
			drilldown: {
				drillUpButton: {
					relativeTo: 'spacingBox',
					position: {
						align: 'left',
						y: 0,
						x: 0,
					},
				},
			},
			lang: {
				drillUpText: i18n('Back') as unknown as object,
			},
		};
	};

	onDrillDownGridEditionThrottled = throttle(
		(chart: Chart, e: DrilldownEventObject) => {
			this.onDrillDownGridEdition(chart, e);
		},
		300,
		{ trailing: false },
	);

	onDrillDownGridEdition = (chart: Chart, e: DrilldownEventObject) => {
		if (!this.enableDrillDown || this.hasCustomRedirectConfig) {
			return;
		}
		const key =
			Object.keys(this.datasourceRuleConfig)
				.filter((key) => this.datasourceRuleConfig[key].type == 'integer')
				.find((key) => this.datasourceRuleConfig[key].label == (e.point.name ?? e.point.series.name)) ??
			e.point.options.name.toString();

		const drillDownStore = new DrilledDownWidgetStore(this, this.props.config, key, e);

		openModal(
			{
				title: '',
				width: 1000,
				height: 500,
				resizable: true,
				footer: null,
				mask: false,
				positionType: ModalPosition.Mouse,
			},
			<DrilledDownModal store={drillDownStore} />,
		);
	};

	onHideLegendChanged = async (val: boolean) => {
		this.props.config.hideLegend = val;
		await this.drawChart();
	};

	onDrillDown = async (chart: Chart, e: DrilldownEventObject) => {
		const key = Object.keys(this.datasourceRuleConfig)
			.filter((key) => this.datasourceRuleConfig[key].type == 'integer')
			.find((key) => this.datasourceRuleConfig[key].label == e.point.name);

		const confRequest = this.getDataSourceFiltersConfiguration(key, e);
		this.datasourceRuleConfig =
			(await apiFetch(confRequest))?.data ?? JSON.parse(JSON.stringify(this.datasourceRuleConfig));

		const dataRequest = this.getDataRequest(key, e);
		this.data = (await apiFetch(dataRequest))?.data;

		const chartData = await this.dataBuilders[this.props.config.chartType]?.(e);
		const series = chartData.find((x) => x.name == e.point.series.name);
		chart.addSeriesAsDrilldown(e.point, series);
	};

	buildPieData = async () => {
		const keys = Object.keys(this.datasourceRuleConfig).filter(
			(key) => this.datasourceRuleConfig[key].type == 'integer',
		);

		const palette = new ColorPalette(this.props.config.colorPalette ?? DEFAULT_COLOR_PALETTE);
		const colorRange = palette.build(this.data.items.length);
		if (!this.props.config.pivotMatrix) {
			this.data.items.sort((a, b) => {
				const rows = this.config.dataRows ?? [];
				return (
					rows.findIndex((x) => x == a.data[CategoryColumnName]) -
					rows.findIndex((x) => x == b.data[CategoryColumnName])
				);
			});
		}

		const seriesData = this.data.items.map((item) => {
			const color =
				this.props.config.legendColors?.find((x) => x.key == item.data[CategoryColumnName])?.color ??
				colorRange.next().value;

			return {
				name: this.datasourceRuleConfig[CategoryColumnName].fieldSettings.listValues.find(
					(x) => x.value == item.data[CategoryColumnName],
				).title,
				y: keys.reduce((r, key) => {
					return r + (item.data[key] ?? 0);
				}, 0),
				drilldown: this.enableDrillDown,
				color: color,
			};
		});

		const data = {
			type: 'pie',
			colorByPoint: this.enableDrillDown,
			data: seriesData,
		};
		const seriesOptions = this.subTypes.find((x) => x.id === this.props.config.chartSubType)?.series;
		if (seriesOptions) {
			Object.assign(data, seriesOptions);
		}
		return [data];
	};

	buildBarData = async () => {
		const keys = Object.keys(this.datasourceRuleConfig).filter(
			(key) => this.datasourceRuleConfig[key].type == 'integer',
		);

		const dataColumns =
			(this.props.config.pivotMatrix ? this.props.config.pivotValues : this.props.config.dataColumns) ?? [];

		keys.sort((a, b) => {
			if (dataColumns.every((x) => !a.startsWith(x)) && dataColumns.every((x) => !b.startsWith(x))) {
				return 0;
			}
			if (dataColumns.some((x) => a.startsWith(x)) && dataColumns.some((x) => !b.startsWith(x))) {
				return dataColumns.findIndex((x) => b.startsWith(x)) - dataColumns.findIndex((x) => a.startsWith(x));
			}
			if (dataColumns.some((x) => a.startsWith(x))) return -1;

			return 1;
		});

		const palette = new ColorPalette(this.props.config.colorPalette ?? DEFAULT_COLOR_PALETTE);
		const colorRange = palette.build(keys.length);
		const dataValues =
			(this.props.config.pivotMatrix ? this.props.config.pivotColumns : this.props.config.dataRows) ?? [];

		this.data.items.sort((a, b) => {
			const aValue = a.data[CategoryColumnName];
			const bValue = b.data[CategoryColumnName];
			return dataValues.findIndex((x) => x == aValue) - dataValues.findIndex((x) => x == bValue);
		});
		const colors = keys.map(() => colorRange.next().value);
		return keys.map((key) => {
			const barColor = this.props.config.legendColors?.find((x) => x.key == key)?.color ?? colors.pop();
			const sData = this.data.items.map((row, rowIndex) => ({
				y: row.data[key] ?? 0,
				drilldown: this.enableDrillDown,
				id: rowIndex,
			}));
			return {
				name: this.datasourceRuleConfig[key].label,
				data: sData,
				stack: (this.subTypeData?.value ?? '').endsWith('-stacked') ? 'main' : barColor,
				color: barColor,
			};
		});
	};

	getDataRequest = (key?: string, e?: DrilldownEventObject) => {
		const dataRequest = getDataSourceData({
			requestId: this.requestId,
			...this.props.config,
			accountId: this.props.config.accountId,
			dataSourceId: this.getDatasourceId(!!key),
		});

		dataRequest.payload = {};
		dataRequest.payload.filter = {};
		dataRequest.payload.sort = [];
		dataRequest.payload.skip = 0;
		dataRequest.payload.take = PSEUDO_FULL_RESPONSE;
		dataRequest.payload.timePeriod = this.timePeriod;
		dataRequest.payload.timePeriodColumn = this.getTimePeriodColumn(!!key);
		dataRequest.payload.requestId = this.requestId;

		if (!key) {
			dataRequest.payload.dataRows = this.props.config.dataRows;
			dataRequest.payload.dataColumns = this.props.config.dataColumns;
		}

		const additionalPayload = this.drillDownRequestPayloadBuilders[this.props.config.chartType]?.(key, e);
		Object.assign(dataRequest.payload, additionalPayload);
		return dataRequest;
	};

	getDataSourceFiltersConfiguration = (key?: string, e?: DrilldownEventObject) => {
		const payload: any = {};
		const additionalPayload = this.drillDownRequestPayloadBuilders[this.props.config.chartType]?.(key, e);
		Object.assign(payload, additionalPayload);
		payload.timePeriod = this.timePeriod;
		payload.timePeriodColumn = this.getTimePeriodColumn(!!key);
		payload.requestId = this.requestId;
		if (!key) {
			payload.dataRows = this.props.config.dataRows;
			payload.dataColumns = this.props.config.dataColumns;
		}

		return new ApiRequest<RulesConfiguration>({
			url: `dataSources/${this.getDatasourceId(!!key)}/ruleConfiguration`,
			accountId: this.props.config.accountId,
			accountBased: true,
			method: 'POST',
			payload,
		});
	};

	buildPieDataPayload = (key?: string, e?: DrilldownEventObject) => {
		const settings = {
			pivotData: this.props.config.pivotData,
			pivotMatrix: this.props.config.pivotMatrix,
			pivotColumns: this.props.config.pivotColumns,
			categoryColumns: this.props.config.categoryColumns,
			pivotValues: this.props.config.pivotValues,
			pivotRows: this.props.config.pivotRows,
			splitPivotValues: this.props.config.splitPivotValues,
		};
		if (e) {
			const keyValue =
				this.datasourceRuleConfig?.[CategoryColumnName].fieldSettings.listValues.find((x) => x.title == key)?.value ??
				key;
			return {
				drillDownField: '', // because for pie aggregated
				drillDownData: key ? this.data.items.find((x) => x.data[CategoryColumnName] == keyValue)?.data : undefined,
				drillDownSettings: settings,
			};
		}
		return settings;
	};

	buildBarDataPayload = (key?: string, e?: DrilldownEventObject) => {
		const settings = {
			pivotData: this.props.config.pivotData,
			pivotMatrix: this.props.config.pivotMatrix,
			pivotColumns: this.props.config.pivotColumns,
			categoryColumns: this.props.config.categoryColumns,
			pivotValues: this.props.config.pivotValues,
			pivotRows: this.props.config.pivotRows,
			splitPivotValues: this.props.config.splitPivotValues,
		};
		if (e) {
			return {
				drillDownField: key,
				drillDownData: key ? this.data.items[e.point.x].data : undefined,
				drillDownSettings: settings,
			};
		}
		return settings;
	};
	closeRowsWarning = () => {
		this.rowsWarningMessage = undefined;
	};
	closeColumnsWarning = () => {
		this.columnsWarningMessage = undefined;
	};
}

export const convertDataSelectorTypeToTimeSelectorType = (dataSourceSelector: {
	type: string;
	value: number;
	fromTime: number;
	toTime: number;
	zoneId: string;
	initializeStart: boolean;
}): TimePeriod => {
	switch (dataSourceSelector?.type) {
		case 'LASTN':
			return { period: TimePeriodType.LastN, lastNValue: dataSourceSelector.value };
		case 'TODAY':
			return { period: TimePeriodType.CurrentDay };
		case 'THIS_WEEK':
			return { period: TimePeriodType.CurrentWeek };
		case 'THIS_MONTH':
			return { period: TimePeriodType.CurrentMonth };
		case 'LAST_DAY':
			return { period: TimePeriodType.Last24Hours };
		case 'LAST_7DAYS':
			return { period: TimePeriodType.Last7Days };
		case 'LAST_30DAYS':
			return { period: TimePeriodType.Last30Days };
		case 'LAST_WEEK':
			return { period: TimePeriodType.Last7Days };
		case 'LAST_MONTH':
			return { period: TimePeriodType.Last30Days };
		case 'MONTHS':
			return { period: TimePeriodType.Month, intervalValue: dataSourceSelector.value };
		case 'PERIOD':
			return {
				period: TimePeriodType.Custom,
				startDate: dataSourceSelector.fromTime,
				endDate: dataSourceSelector.toTime,
			};
		case 'DAYS':
			return {
				period: TimePeriodType.DynamicInterval,
				intervalType: IntervalType.Days,
				intervalValue: dataSourceSelector.value,
			};
		case 'HOURS':
			return {
				period: TimePeriodType.DynamicInterval,
				intervalType: IntervalType.Hours,
				intervalValue: dataSourceSelector.value,
			};
		case 'MINUTES':
			return {
				period: TimePeriodType.DynamicInterval,
				intervalType: IntervalType.Minutes,
				intervalValue: dataSourceSelector.value,
			};
		case 'SECONDS':
			return {
				period: TimePeriodType.DynamicInterval,
				intervalType: IntervalType.Seconds,
				intervalValue: dataSourceSelector.value,
			};
		case 'LAST_QUARTER':
		case 'THIS_QUARTER':
		default:
			return { period: TimePeriodType.LastN, lastNValue: 1 };
	}
};

type ColumnOptions = { value: string; label: string; type?: string };

export class DataSourcesChartWidgetConfigurationStore implements CommonSettingsDatasourceConfigStore<DataSourcesChartWidgetConfigModel> {
	model: DataSourcesChartWidgetConfigModel;
	manager: DataSourceManager;
	pivotManager: PivotManager<PivotModel>;
	requestId = newGuid();

	chartTypes: ChartTypeOptions[];
	pieSubTypes: ChartTypeOptions[];
	barSubTypes: ChartTypeOptions[];

	colorColumnOptions: ColumnOptions[] = [];
	enableTimeSelector: boolean;
	defaultTimeSelector: TimePeriod;
	currentDataSource: DataSource;
	dataSources?: DataSource[];

	mobx = new MobxManager();

	constructor(public props: WidgetConfigurationProps<DataSourcesChartWidgetConfig>) {
		const configWithDefaults = Object.assign({ chartType: 'bar' }, props.config);
		this.model = new DataSourcesChartWidgetConfigModel(configWithDefaults);

		this.manager = new DataSourceManager({
			selection: this.model,
			loadDatasourceList: true,
			requestId: this.requestId,
		});
		this.pivotManager = new PivotManager({ model: this.model, requestId: this.requestId });

		this.props.setValidator(this.model.validator);

		this.props.setFormConfigUpdater(() => {
			Object.assign(this.props.config, this.model.getConfig());
		});

		makeObservable(this, {
			model: true,
			chartTypes: true,
			pieSubTypes: true,
			barSubTypes: true,
			colorColumnOptions: true,
			enableTimeSelector: true,
			defaultTimeSelector: true,
			currentDataSource: true,
			dataSources: true,
			timeColumns: computed,
		});

		this.mobx.reaction(
			() => this.timeColumns,
			(timeColumns) => applyTimeColumnDefaults(timeColumns, this, this.model),
		);

		this.mobx.reaction(
			() => this.model.dataSourceId,
			() => this.pivotManager.closeColumnsWarning(),
		);

		this.mobx.reaction(
			() => this.model.dataSourceId,
			(id) => {
				if (!this.dataSources) return;
				this.currentDataSource = this.dataSources?.find((x) => x.id == id);
				this.defaultTimeSelector = convertDataSelectorTypeToTimeSelectorType(this.currentDataSource?.dataSelector);
			},
		);

		this.mobx.reaction(
			() => this.colorColumnOptions,
			() => {
				this.model.legendColors = this.model.legendColors?.filter((y) =>
					this.colorColumnOptions.some((x) => x.value == y.key),
				);
			},
		);

		this.mobx.reaction(
			() => this.model.dataColumns,
			(dataColumns) => {
				this.model.legendColors = this.model.legendColors.filter((x) => dataColumns.some((y) => y.startsWith(x.key)));
			},
		);

		this.mobx.reaction(
			() => this.model.pivotData,
			() => {
				this.model.legendColors = [];
			},
		);

		this.mobx.reaction(
			() => [this.model.dataRows?.length, this.model.dataColumns?.length],
			() => this.fillColorsLegend(),
		);
		this.mobx.reaction(
			() => [this.model.pivotColumns?.length, this.model.pivotValues?.length],
			() => this.fillColorsLegend(),
		);
		this.mobx.reaction(
			() => [this.model.pivotMatrix, this.model.chartType, this.model.pivotData],
			() => this.fillColorsLegend(),
		);
		this.mobx.reaction(
			() => this.enableTimeSelector,
			() => {
				if (this.enableTimeSelector) {
					this.model.period ??= { ...this.defaultTimeSelector };
				} else {
					this.model.period = undefined;
				}
			},
		);
	}

	get timeColumns() {
		return getTimeColumns(this.manager.rulesConfiguration);
	}

	init = async () => {
		this.model.decimalsNumber ??= 0;
		this.model.legendColors ??= [];
		this.enableTimeSelector = !!this.model.period;
		this.chartTypes = getChartTypeOptions();
		this.pieSubTypes = getPieSubTypeOptions();
		this.barSubTypes = getBarSubTypeOptions();
		await this.fillColorsLegend();

		this.dataSources =
			(
				await apiFetch(
					getDataSources({
						accountId: this.model.accountId,
						dataSourceSelectorAccountId: this.model.dataSourceSelectorAccountId,
					}),
				)
			)?.data ?? [];
		this.currentDataSource = this.dataSources.find((x) => x.id == this.model.dataSourceId);
		this.defaultTimeSelector = convertDataSelectorTypeToTimeSelectorType(this.currentDataSource?.dataSelector);
	};

	getDataRequest = () => {
		const dataRequest = getDataSourceData({
			requestId: this.requestId,
			...this.model,
			accountId: this.model.accountId,
		});
		dataRequest.payload = {
			pivotMatrix: this.model.pivotMatrix,
			pivotColumns: this.model.pivotColumns,
			pivotRows: this.model.pivotRows,
			pivotValues: this.model.pivotValues,
			categoryColumns: this.model.categoryColumns,
			dataColumns: this.model.dataColumns,
			dataRows: this.model.dataRows,
			requestId: this.requestId,
		};
		dataRequest.payload.filter = {};
		dataRequest.payload.sort = [];
		dataRequest.payload.skip = 0;
		dataRequest.payload.take = PSEUDO_FULL_RESPONSE;
		return dataRequest;
	};

	fillColorsLegend = async () => {
		if (this.model.pivotMatrix) {
			if (
				(this.model.pivotData && this.model.chartType != 'pie') ||
				(!this.model.pivotData && this.model.chartType == 'pie')
			) {
				const data = await apiFetch(this.getDataRequest());
				this.colorColumnOptions = data.data.items.map((x) => ({
					label: x.data[CategoryColumnName] as string,
					value: x.data[CategoryColumnName] as string,
				}));
			} else {
				const config = await apiFetch(
					getDataSourceFiltersConfiguration({
						requestId: this.requestId,
						...this.model,
						accountId: this.model.accountId,
					}),
				);
				this.colorColumnOptions = Object.keys(config.data)
					.filter((key) => config.data[key].type == FieldType.Number)
					.map((key) => ({ label: config.data[key].label, value: key }));
			}
			return;
		}

		const columnOptionsSource =
			(this.model.pivotData && this.model.chartType != 'pie') ||
			(!this.model.pivotData && this.model.chartType == 'pie')
				? this.pivotManager.rowsOptions
				: this.pivotManager.columnsOptions;

		const selectedLegend =
			(this.model.pivotData && this.model.chartType != 'pie') ||
			(!this.model.pivotData && this.model.chartType == 'pie')
				? this.model.dataRows?.length
					? this.model.dataRows
					: this.pivotManager.rowsOptions.map((x) => x.value)
				: this.model.dataColumns?.length
					? this.model.dataColumns
					: this.pivotManager.columnsOptions.map((x) => x.value);

		this.colorColumnOptions = columnOptionsSource.filter((x) => selectedLegend.includes(x.value));
	};

	destroy() {
		this.mobx.destroy();
		this.pivotManager.destroy();
		this.manager.destroy();
		this.model.destroy();
		void apiFetch(
			clearDatasourceCache({ accountId: this.model.accountId, requestId: this.requestId }),
		);
	}
}

export const DataSourcesChartWidgetConfigurationContext =
	React.createContext<DataSourcesChartWidgetConfigurationStore>(null);
